import React, { useState, useRef, useEffect, useContext } from "react";
import { Switch, Spin, Text, Tooltip } from "react_reusable_components";
import { TwoColumn, TwoRow } from "../../../core/layouts";
import { useIntl } from "react-intl";

import useResponsive from "../../../core/hooks/useResponsive";
import ThreeColumn from "../../../core/layouts/ThreeColumn";
import variables from "../../../themes/base/styles/variables";
import Graph from "../DashboardGraphs/Graphs/RadarGraph/Graph";
import TableActions from "../../../components/TableActions";
import DashboardService from "../../../services/dashboard-service";
import { DashboardContext } from "../../../globalContext/dashboard/dashboardProvider";
import { setRadarGraph } from "../../../globalContext/dashboard/dashboardActions";
import { GlobalLoaderContext } from "../../../globalContext/globalLoader/globalLoaderProvider";
import { getDashboardQueryParams, getLocalizedMessage } from "../../../UtilityFunctions/utils";
import { BAD_REQUEST } from "../../../constants/errorConstants";
import { InfoCircleOutlined } from "@ant-design/icons";
import { GRAPH_TOOLTIP, userRole } from "../../../constants";
import GraphTooltipContainer from "../../GraphTooltipContainer";
import { AuthContext } from "../../../globalContext/auth/authProvider";

export const RadarGraphStyles = {
  radarBox: {
    border: variables.border,
    padding: variables.paddingSmall,
    backgroundColor: variables.bodyBg,
    minHeight: variables.graphsMinHeight,
  },
  headerSection: {
    justifyContent: variables.spaceBetween,
    alignItems: variables.alignCenter,
  },
  heading: {
    fontWeight: variables.fontWeightBold,
    fontSize: variables.fontSizeXlarge,
  },
  moreOutlinedIcon: {
    paddingTop: variables.moreOutlinedIconPaddingTop,
  },
  switch: {
    gap: variables.gapXS1,
    fontWeight: variables.switchLabelFontWeight,
    spaceBetween: variables.spaceBetween,
    alignItems: variables.alignCenter,
  },
  iconLabel: {
    gap: variables.gapXS1,
    alignItems: variables.alignCenter,
  },
  bottomSection: {
    paddingTop: variables.paddingSmall,
    // height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  mobileSwitch: {
    fontSize: variables.fontSizeSmall,
    iconWidth: variables.iconWidth,
  },
  spinner: {
    height: variables.spinnerAndChooseFilterTextHeight,
    alignItems: variables.alignCenter,
    justifyContent: variables.justifyCenter,
    display: variables.dFlex,
  },
  notFoundText: {
    fontSize: variables.fontSizeXlarge,
    textAlign: variables.alignCenter,
    width: variables.fullWidth,
  },
  tooltipWidth: { width: variables.tooltipWidth },
};

const RadarGraph = ({ filter }) => {
  const responsive = useResponsive();
  const intl = useIntl();
  const [checked, setChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [authState] = useContext(AuthContext);
  const [dashboardState, dashboardDispatch] = useContext(DashboardContext);
  const [radarData, setRadarData] = useState([]);
  const [dataNotFound, setDataNotFound] = useState("");
  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const screenWidth = window.innerWidth;

  useEffect(() => {
    setIsLoading(true);
    let params = getDashboardQueryParams(filter);
    delete params.isPercentage;
    if (
      !params.year ||
      (params.type === "Repair" && !params.subProductCategoryId) ||
      (authState?.currentRole?.id === userRole.ADMIN && !params.countryId) ||
      !params.type
    ) {
      setDataNotFound(
        getLocalizedMessage(
          intl,
          authState?.currentRole?.id !== userRole.ADMIN
            ? "selectCategories.CA.TM.label"
            : "selectCategories.label"
        )
      );
    } else {
      if (dataNotFound) {
        setDataNotFound("");
      }
      DashboardService.getCountLevelApiCall({
        dispatch: dashboardDispatch,
        intl,
        params: {
          ...params,
          level: 1,
        },
      })
        .then((data) => {
          dashboardDispatch(setRadarGraph(data.data));
        })
        .catch((error) => {
          if (error?.response?.data?.code === BAD_REQUEST) {
            setDataNotFound(
              getLocalizedMessage(intl, "dataNotAvailable.label")
            );
          }
        });
    }
    setIsLoading(false);
  }, [filter]);

  useEffect(() => {
    if (dashboardState.radarGraphData.result) {
      const newData = dashboardState?.radarGraphData?.result?.map(
        (item, index) => ({
          okCount: dashboardState.radarGraphData.result[index].okCount,
          section: `${
            dashboardState.radarGraphData.result[index].okCount +
            "/" +
            dashboardState.radarGraphData.result[index].questionCount
          } ${dashboardState.radarGraphData.result[index].sectionName}`,
          questionCount:
            dashboardState.radarGraphData.result[index].questionCount,
          percentage: dashboardState.radarGraphData.result[index].percentage,
          sectionName: dashboardState.radarGraphData.result[index].sectionName,
        })
      );
      setChecked(false)
      setRadarData(newData);
    }
  }, [dashboardState]);

  useEffect(() => {
    setIsLoading(true);
    const convertedData = radarData.map((item) => {
      if (checked) {
        item.percentage = Number(((item.okCount / item.questionCount) * 100).toFixed(
          1
        ));
        item.section = `${item.percentage}% ${item.sectionName}`;
      } else {
        item.percentage = (item.okCount / item.questionCount) * 100;
        item.section = `${item.okCount}/${item.questionCount} ${item.sectionName}`;
      }
      return {
        ...item,
      };
    });
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
    setRadarData(convertedData);
  }, [checked]);

  const downloadUserReportsHandler = () => {
    let queryParams = getDashboardQueryParams(filter);
    delete queryParams.isPercentage;
    DashboardService.dashboardDownloadReportApiCall({
      params: {
        ...queryParams,
        fileName: getLocalizedMessage(intl, "dashboard.radarGraph.label"),
        reportType: getLocalizedMessage(intl, "levelReport.label"),
        level: 1,
      },
      intl,
      loaderDispatch: loaderDispatch,
    });
  };

  const [graphMounted, setGraphMounted] = useState(false);

  // Use a useRef to access the Graph component
  const graphRef = useRef();

  useEffect(() => {
    setGraphMounted(true);
  }, []);

  const downloadChartImage = () => {
    // Call the downloadChartImage function of the Graph component only if it is mounted
    try {
      if (graphMounted && graphRef.current) {
        graphRef.current.downloadChartImage();
      }
    } catch (error) {
      setDataNotFound(error);
    }
  };

  return (
    <TwoRow
      id="radar-graph"
      style={RadarGraphStyles.radarBox}
      topSection={
        <TwoColumn
          style={RadarGraphStyles.headerSection}
          leftSection={
            <Text type="secondary" style={RadarGraphStyles.heading}>
              {getLocalizedMessage(intl, "dashboard.radarGraph.label")}{" "}
              <Tooltip
                overlayInnerStyle={RadarGraphStyles.tooltipWidth}
                title={
                  <GraphTooltipContainer
                    data={GRAPH_TOOLTIP(intl).RADAR_GRAPH}
                  />
                }
              >
                <InfoCircleOutlined />
              </Tooltip>
            </Text>
          }
          rightSection={
            dataNotFound || !radarData?.length ? null : (
              <ThreeColumn
                style={RadarGraphStyles.switch}
                leftSection={
                  responsive.isMd ? (
                    <Text type="secondary" style={RadarGraphStyles.switch}>
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  ) : (
                    <Text
                      type="secondary"
                      style={RadarGraphStyles.mobileSwitch}
                    >
                      {getLocalizedMessage(intl, "dashboard.switchNumberLabel")}
                    </Text>
                  )
                }
                middleSection={
                  <Switch
                    size={responsive.isMd ? "default" : "small"}
                    onChange={() => setChecked(!checked)}
                    checked={checked}
                  />
                }
                rightSection={
                  <TwoColumn
                    style={RadarGraphStyles.iconLabel}
                    leftSection={
                      responsive.isMd ? (
                        <Text type="secondary" style={RadarGraphStyles.switch}>
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      ) : (
                        <Text
                          type="secondary"
                          style={RadarGraphStyles.mobileSwitch}
                        >
                          {getLocalizedMessage(
                            intl,
                            "dashboard.switchPercentageLabel"
                          )}
                        </Text>
                      )
                    }
                    rightSection={
                      <TableActions
                        tableActionsOptions={[
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadGraph.label"
                            ),
                            action: downloadChartImage,
                          },
                          {
                            label: getLocalizedMessage(
                              intl,
                              "downloadReport.label"
                            ),
                            action: downloadUserReportsHandler,
                          },
                        ]}
                      />
                    }
                  />
                }
              />
            )
          }
        />
      }
      bottomSectionStyle={
        isLoading ? RadarGraphStyles.spinner : RadarGraphStyles.bottomSection
      }
      bottomSection={
        isLoading ? (
          <Spin size={"large"} type={"default"} />
        ) : !dataNotFound && radarData?.length ? (
          <Graph
            ref={graphRef}
            id="graph"
            RadarGraphStyles={RadarGraphStyles}
            data={radarData}
            width={"100%"}
            height={screenWidth < 768 ? 250 : 400}
            cx={"50%"}
            cy={"50%"}
            outerRadius={"61%"}
            gridType={"circle"}
            dataKey1={"section"}
            dataKey2={checked ? "percentage" : "okCount"}
            stroke={variables.cyan2}
            fill={variables.lightBlue2}
            fillOpacity={0.9}
            domain={checked && [0, 100]}
            linkLabel={getLocalizedMessage(intl, "radar.link.label")}
          />
        ) : (
          <Text
            style={{
              ...RadarGraphStyles.notFoundText,
              marginTop: responsive.isSm ? "20%" : "40%",
            }}
          >
            {dataNotFound}
          </Text>
        )
      }
    />
  );
};

export default RadarGraph;
